import { useState } from 'react';
import CharacterCount from '@tiptap/extension-character-count';
import Placeholder from '@tiptap/extension-placeholder';
import { useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useTranslation } from 'react-i18next';
import { useAttachmentUploads } from '@/hooks/useAttachmentUploads';
import { useComposeDraft } from '@/pages/Mail/hooks/useComposeDraft';
import { useMailMessage } from '@/pages/Mail/hooks/useMailMessage';
import { MAIL_FOLDER_CONFIG } from '@/pages/Mail/mailFolders';
import {
  AttachmentUploadOrigin,
  MailComposeMode,
  MailDraftType,
  MailFolderType,
  MailSubjectPrefix,
} from '@/types/components';
import type { MailMessage, Recipient } from '@/types/mail';
import { Capitalize } from '@/utils/editor';
import { ErrorMessage, logRequestError } from '@/utils/errors';
import { isBlank } from '@/utils/text';

const MAX_BODY_LENGTH = 10000;
const NO_PREVIOUS_MESSAGE_ID = 0;

const subjectPrefixes: Record<MailComposeMode, MailSubjectPrefix> = {
  [MailComposeMode.New]: MailSubjectPrefix.None,
  [MailComposeMode.Draft]: MailSubjectPrefix.None,
  [MailComposeMode.Reply]: MailSubjectPrefix.Reply,
  [MailComposeMode.Forward]: MailSubjectPrefix.Forward,
};

const draftTypes: Record<MailComposeMode, MailDraftType> = {
  [MailComposeMode.New]: MailDraftType.New,
  [MailComposeMode.Draft]: MailDraftType.New,
  [MailComposeMode.Reply]: MailDraftType.Reply,
  [MailComposeMode.Forward]: MailDraftType.Forward,
};

const getReplyRecipient = (
  mail: MailMessage,
  folderType: MailFolderType,
): Recipient | null => {
  if (MAIL_FOLDER_CONFIG[folderType].repliesToFirstRecipient) {
    const [firstRecipient] = mail.recipients;

    if (!firstRecipient) {
      return null;
    }

    return {
      id: Number(firstRecipient.id),
      name: firstRecipient.name,
      description: '',
    };
  }

  if (!mail.authorUserId) {
    return null;
  }

  return { id: mail.authorUserId, name: mail.senderName, description: '' };
};

interface Params {
  mode: MailComposeMode;
  folderType: MailFolderType;
  sourceMail?: MailMessage | null;
  onSent: () => void;
}

export const useComposeForm = ({
  mode,
  folderType,
  sourceMail,
  onSent,
}: Params) => {
  const { t } = useTranslation();
  const isDraft = mode === MailComposeMode.Draft && !!sourceMail;
  const initialSubject = `${subjectPrefixes[mode]}${sourceMail?.subject ?? ''}`;
  const initialBody = isDraft ? sourceMail.body : '';
  const replyRecipient =
    mode === MailComposeMode.Reply && sourceMail
      ? getReplyRecipient(sourceMail, folderType)
      : null;
  const initialRecipients = isDraft
    ? sourceMail.draftRecipients
    : replyRecipient
      ? [replyRecipient]
      : [];
  const messageType = isDraft
    ? (sourceMail.draftMessageType ?? MailDraftType.New)
    : draftTypes[mode];
  const previousMessageId = isDraft
    ? (sourceMail.draftPreviousMessageId ?? NO_PREVIOUS_MESSAGE_ID)
    : Number(sourceMail?.id ?? NO_PREVIOUS_MESSAGE_ID);
  const isReplyOrForward = messageType !== MailDraftType.New;
  const quotedDraftMessageId =
    isDraft && isReplyOrForward ? String(previousMessageId) : null;

  const [subject, setSubject] = useState(initialSubject);
  const [recipients, setRecipients] = useState<Recipient[]>(initialRecipients);
  const [showErrors, setShowErrors] = useState(false);
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSendErrorOpen, setIsSendErrorOpen] = useState(false);

  const {
    attachments,
    uploadedAttachments,
    isReadyForSending,
    addFiles,
    removeAttachment,
  } = useAttachmentUploads({
    origin: AttachmentUploadOrigin.Mail,
    initialAttachments: isDraft ? sourceMail.attachments : [],
  });

  const { mail: previousMail } = useMailMessage(quotedDraftMessageId);
  const quotedMail = isDraft ? previousMail : (sourceMail ?? null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false } }),
      CharacterCount.configure({ limit: MAX_BODY_LENGTH }),
      Capitalize,
      Placeholder.configure({
        placeholder: t('mail.composeModal.bodyPlaceholder'),
      }),
    ],
    content: initialBody,
    onFocus: () => setIsEditorFocused(true),
    onBlur: () => setIsEditorFocused(false),
  });

  const bodyState = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      html: instance?.getHTML() ?? '',
      isEmpty: instance?.isEmpty ?? true,
      charCount: instance?.storage.characterCount.characters() ?? 0,
    }),
  });
  const body = bodyState?.html ?? '';
  const isBodyEmpty = bodyState?.isEmpty ?? true;
  const isCharLimitReached = (bodyState?.charCount ?? 0) >= MAX_BODY_LENGTH;

  const isRecipientsEmpty = recipients.length === 0;
  const isSubjectEmpty = isBlank(subject);
  const isInvalid = isRecipientsEmpty || isSubjectEmpty || isBodyEmpty;

  const errorRecipients = showErrors && isRecipientsEmpty;
  const errorSubject = showErrors && isSubjectEmpty;
  const errorBody = showErrors && isBodyEmpty;

  const { quote, sendDraft } = useComposeDraft({
    subject,
    body,
    recipients,
    attachments: uploadedAttachments,
    hasContent: !isSubjectEmpty || !isBodyEmpty,
    messageType,
    previousMessageId,
    draftId: isDraft ? Number(sourceMail.id) : undefined,
  });

  const handleSend = async () => {
    if (isInvalid) {
      setShowErrors(true);
      return;
    }

    setIsSending(true);

    try {
      await sendDraft();
      onSent();
    } catch (error) {
      logRequestError(ErrorMessage.SendMailMessage, error);
      setIsSendErrorOpen(true);
      setIsSending(false);
    }
  };

  return {
    editor,
    subject,
    setSubject,
    recipients,
    setRecipients,
    attachments,
    addFiles,
    removeAttachment,
    quote,
    quotedMail,
    isEditorFocused,
    isCharLimitReached,
    isSending,
    isSendErrorOpen,
    canSend: isReadyForSending,
    errorRecipients,
    errorSubject,
    errorBody,
    handleSend,
    dismissSendError: () => setIsSendErrorOpen(false),
  };
};
